// ABOUTME: Utility functions for Temporal API operations to replace moment.js functionality
// ABOUTME: Provides helper methods for date/time manipulation, formatting, and comparison

// Import Temporal from our isolated export to avoid build issues
import { Temporal } from './temporal-export';

// Define DurationLike type interface since it's not exported from the polyfill
type DurationLike = {
  years?: number;
  months?: number;
  weeks?: number;
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  milliseconds?: number;
  microseconds?: number;
  nanoseconds?: number;
};

/**
 * Convert a Date object to a Temporal.Instant
 * This replaces the global Date.prototype.toTemporalInstant to avoid build issues
 */
export function dateToTemporalInstant(date: Date): Temporal.Instant {
  // Use the Date's timestamp to create a Temporal.Instant
  return Temporal.Instant.fromEpochMilliseconds(date.getTime());
}

export type DateInput =
  | string
  | Date
  | Temporal.PlainDate
  | Temporal.PlainDateTime
  | Temporal.Instant
  | Temporal.ZonedDateTime;
export type DateTimeInput = string | Date | Temporal.PlainDateTime | Temporal.Instant | Temporal.ZonedDateTime;

/**
 * Parse a date string into a Temporal.PlainDate
 * Returns null for invalid dates to match moment.js behavior
 */
export function parseDate(input: DateInput): Temporal.PlainDate | null {
  try {
    if (typeof input === 'string') {
      if (!input.trim()) {
        return null;
      }
      // ISO datetime strings with timezone
      if (input.includes('T') || input.includes('Z')) {
        const instant = Temporal.Instant.from(input);
        return instant.toZonedDateTimeISO('UTC').toPlainDate();
      }

      return Temporal.PlainDate.from(input);
    }
    if (input instanceof Date) {
      // Check for invalid Date objects
      if (isNaN(input.getTime())) {
        return null;
      }
      const instant = dateToTemporalInstant(input);
      return instant.toZonedDateTimeISO('UTC').toPlainDate();
    }
    if (input instanceof Temporal.PlainDate) {
      return input;
    }
    if (input instanceof Temporal.PlainDateTime) {
      return input.toPlainDate();
    }
    if (input instanceof Temporal.Instant) {
      return input.toZonedDateTimeISO('UTC').toPlainDate();
    }
    if (input instanceof Temporal.ZonedDateTime) {
      return input.toPlainDate();
    }
    return null;
  } catch {
    // Return null for any parsing errors to match moment.js behavior
    return null;
  }
}

/**
 * Parse a datetime string into a Temporal.PlainDateTime
 * Returns null for invalid input to match moment.js behavior
 */
export function parseDateTime(input: DateTimeInput): Temporal.PlainDateTime | null {
  try {
    if (typeof input === 'string') {
      // Try to parse as PlainDateTime first, then as PlainDate
      try {
        if (input.includes('Z')) {
          const instant = Temporal.Instant.from(input);
          return instant.toZonedDateTimeISO('UTC').toPlainDateTime();
        }
        return Temporal.PlainDateTime.from(input);
      } catch {
        const date = Temporal.PlainDate.from(input);
        return date.toPlainDateTime({ hour: 0, minute: 0, second: 0 });
      }
    }
    if (input instanceof Date) {
      // Check for invalid Date objects
      if (isNaN(input.getTime())) {
        return null;
      }
      const instant = dateToTemporalInstant(input);
      return instant.toZonedDateTimeISO('UTC').toPlainDateTime();
    }
    if (input instanceof Temporal.PlainDateTime) {
      return input;
    }
    if (input instanceof Temporal.Instant) {
      return input.toZonedDateTimeISO('UTC').toPlainDateTime();
    }
    if (input instanceof Temporal.ZonedDateTime) {
      return input.toPlainDateTime();
    }
    return null;
  } catch {
    // Return null for any parsing errors to match moment.js behavior
    return null;
  }
}

/**
 * Parse into a Temporal.Instant (for timestamps)
 * Returns null for invalid input to match moment.js behavior
 */
export function parseInstant(input: DateTimeInput | number): Temporal.Instant | null {
  try {
    if (typeof input === 'number') {
      // Assume milliseconds if large number, seconds if small
      if (input > 9999999999) {
        return Temporal.Instant.fromEpochMilliseconds(input);
      }
      return Temporal.Instant.fromEpochNanoseconds(BigInt(input) * BigInt(1000000000));
    }
    if (typeof input === 'string') {
      if (!input.trim()) {
        return null;
      }
      return Temporal.Instant.from(input);
    }
    if (input instanceof Date) {
      // Check for invalid Date objects
      if (isNaN(input.getTime())) {
        return null;
      }
      return dateToTemporalInstant(input);
    }
    if (input instanceof Temporal.Instant) {
      return input;
    }
    if (input instanceof Temporal.ZonedDateTime) {
      return input.toInstant();
    }
    if (input instanceof Temporal.PlainDateTime) {
      // Assume UTC for PlainDateTime
      return input.toZonedDateTime('UTC').toInstant();
    }
    return null;
  } catch {
    // Return null for any parsing errors to match moment.js behavior
    return null;
  }
}

/**
 * Helper function to get month names for formatting
 */
const getMonthName = (month: number, format: 'short' | 'long' = 'short'): string => {
  const shortMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
  const longMonths = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  return format === 'short' ? shortMonths[month - 1] : longMonths[month - 1];
};

/**
 * Helper function to pad numbers with leading zeros
 */
const padZero = (num: number): string => String(num).padStart(2, '0');

/**
 * Format a date using locale string options (replaces moment.format())
 * Note: We manually format dates to avoid a bug in @js-temporal/polyfill where
 * toLocaleString fails with "Cannot read properties of undefined (reading 'getPossibleInstantsFor')"
 * See: https://github.com/js-temporal/temporal-polyfill/issues/268
 */
export function formatDate(
  date: DateInput | DateTimeInput | number,
  format: 'YYYY-MM-DD' | 'YYYY-MM' | 'DD/MM/YYYY' | 'DD MMM YYYY' | 'D MMM YYYY' | 'HH:mm' | string = 'YYYY-MM-DD',
): string {
  // Handle time-only formats by parsing as datetime
  if (format === 'HH:mm') {
    // Handle PlainDate inputs by converting to datetime at midnight
    if (date instanceof Temporal.PlainDate) {
      const plainDateTime = date.toPlainDateTime({ hour: 0, minute: 0 });
      return `${padZero(plainDateTime.hour)}:${padZero(plainDateTime.minute)}`;
    }

    // Convert to system timezone (matching moment.js behavior which used browser's local timezone)
    // Use Temporal.Now.timeZoneId() to get the browser's timezone
    const systemTimeZone = Temporal.Now.timeZoneId();
    let zonedDateTime: Temporal.ZonedDateTime | null = null;

    if (typeof date === 'number') {
      const instant = parseInstant(date);
      if (!instant) {
        return 'Invalid time';
      }
      zonedDateTime = instant.toZonedDateTimeISO(systemTimeZone);
    } else if (typeof date === 'string') {
      if (date.includes('T') || date.includes('Z')) {
        const instant = Temporal.Instant.from(date);
        zonedDateTime = instant.toZonedDateTimeISO(systemTimeZone);
      } else {
        const plainDateTime = parseDateTime(date);
        if (!plainDateTime) {
          return 'Invalid time';
        }
        return `${padZero(plainDateTime.hour)}:${padZero(plainDateTime.minute)}`;
      }
    } else if (date instanceof Date) {
      const instant = dateToTemporalInstant(date);
      zonedDateTime = instant.toZonedDateTimeISO(systemTimeZone);
    } else if (date instanceof Temporal.Instant) {
      zonedDateTime = date.toZonedDateTimeISO(systemTimeZone);
    } else if (date instanceof Temporal.ZonedDateTime) {
      zonedDateTime = date.withTimeZone(systemTimeZone);
    } else if (date instanceof Temporal.PlainDateTime) {
      // For PlainDateTime, assume it's already in the correct timezone
      return `${padZero(date.hour)}:${padZero(date.minute)}`;
    }

    if (!zonedDateTime) {
      return 'Invalid time';
    }

    return `${padZero(zonedDateTime.hour)}:${padZero(zonedDateTime.minute)}`;
  }

  // Handle number (timestamp) by converting to Instant first for date parsing
  let dateInput: DateInput;
  if (typeof date === 'number') {
    const instant = parseInstant(date);
    if (!instant) {
      return 'Invalid date';
    }
    dateInput = instant;
  } else {
    dateInput = date;
  }
  const plainDate = parseDate(dateInput);

  // Return "Invalid date" for null input to match moment.js behavior
  if (!plainDate) {
    return 'Invalid date';
  }

  // Extract date components
  const { year } = plainDate;
  const { month } = plainDate;
  const { day } = plainDate;

  switch (format) {
    case 'YYYY-MM-DD':
      return plainDate.toString();
    case 'YYYY-MM':
      return `${year}-${padZero(month)}`;
    case 'DD/MM/YYYY':
      // Manual formatting to avoid toLocaleString bug
      return `${padZero(day)}/${padZero(month)}/${year}`;
    case 'DD MM YYYY':
      return `${padZero(day)} ${padZero(month)} ${year}`;
    case 'DD MMM YYYY':
      // Manual formatting to avoid toLocaleString bug
      return `${padZero(day)} ${getMonthName(month, 'short')} ${year}`;
    case 'D MMM YYYY':
      // Manual formatting to avoid toLocaleString bug
      return `${day} ${getMonthName(month, 'short')} ${year}`;
    case 'MMMM YYYY':
      // Manual formatting to avoid toLocaleString bug
      return `${getMonthName(month, 'long')} ${year}`;
    case 'MMM DD':
      // Manual formatting to avoid toLocaleString bug
      return `${getMonthName(month, 'short')} ${padZero(day)}`;
    case 'DD MMM':
      // Manual formatting to avoid toLocaleString bug
      return `${padZero(day)} ${getMonthName(month, 'short')}`;
    case 'MMM YYYY':
      // Manual formatting to avoid toLocaleString bug
      return `${getMonthName(month, 'short')} ${year}`;
    default:
      // For custom formats, use the default toString
      return plainDate.toString();
  }
}

/**
 * Format a datetime
 * Note: We manually format times to avoid a bug in @js-temporal/polyfill where
 * toLocaleString fails with "Cannot read properties of undefined (reading 'getPossibleInstantsFor')"
 */
export function formatDateTime(dateTime: DateTimeInput, includeTime = true, includeSeconds = false): string {
  const plainDateTime = parseDateTime(dateTime);

  // Return "Invalid date" for null input to match moment.js behavior
  if (!plainDateTime) {
    return 'Invalid date';
  }

  if (!includeTime) {
    return formatDate(plainDateTime.toPlainDate());
  }

  // Manual formatting to avoid toLocaleString bug
  const { year } = plainDateTime;
  const { month } = plainDateTime;
  const { day } = plainDateTime;
  const { hour } = plainDateTime;
  const { minute } = plainDateTime;
  const { second } = plainDateTime;

  const dateStr = `${day} ${getMonthName(month, 'short')} ${year}`;
  const timeStr = includeSeconds
    ? `${padZero(hour)}:${padZero(minute)}:${padZero(second)}`
    : `${padZero(hour)}:${padZero(minute)}`;

  return `${dateStr}, ${timeStr}`;
}

/**
 * Format time only (HH:mm or hh:mm A)
 * Note: We manually format times to avoid a bug in @js-temporal/polyfill where
 * toLocaleString fails with "Cannot read properties of undefined (reading 'getPossibleInstantsFor')"
 */
export function formatTime(dateTime: DateTimeInput, use24Hour = true): string {
  const plainDateTime = parseDateTime(dateTime);

  // Return "Invalid date" for null input to match moment.js behavior
  if (!plainDateTime) {
    return 'Invalid date';
  }

  // Manual formatting to avoid toLocaleString bug
  const { hour } = plainDateTime;
  const { minute } = plainDateTime;

  if (use24Hour) {
    return `${padZero(hour)}:${padZero(minute)}`;
  }
  const isPM = hour >= 12;
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const period = isPM ? 'PM' : 'AM';
  return `${padZero(hour12)}:${padZero(minute)} ${period}`;
}

/**
 * Get current date
 */
export function now(): Temporal.PlainDate {
  return Temporal.Now.plainDateISO();
}

/**
 * Get current datetime
 */
export function nowDateTime(): Temporal.PlainDateTime {
  return Temporal.Now.plainDateTimeISO();
}

/**
 * Get current instant (timestamp)
 */
export function nowInstant(): Temporal.Instant {
  return Temporal.Now.instant();
}

/**
 * Add duration to a date
 */
export function addToDate(date: DateInput, duration: DurationLike): Temporal.PlainDate | null {
  const parsed = parseDate(date);
  if (!parsed) {
    return null;
  }

  // Temporal doesn't support fractional values for days, months, years
  // Round fractional values to avoid "unsupported fractional value" error
  const roundedDuration: DurationLike = {};

  if (duration.years !== undefined) roundedDuration.years = Math.round(duration.years);
  if (duration.months !== undefined) roundedDuration.months = Math.round(duration.months);
  if (duration.weeks !== undefined) roundedDuration.weeks = Math.round(duration.weeks);
  if (duration.days !== undefined) roundedDuration.days = Math.round(duration.days);
  if (duration.hours !== undefined) roundedDuration.hours = duration.hours;
  if (duration.minutes !== undefined) roundedDuration.minutes = duration.minutes;
  if (duration.seconds !== undefined) roundedDuration.seconds = duration.seconds;
  if (duration.milliseconds !== undefined) roundedDuration.milliseconds = duration.milliseconds;
  if (duration.microseconds !== undefined) roundedDuration.microseconds = duration.microseconds;
  if (duration.nanoseconds !== undefined) roundedDuration.nanoseconds = duration.nanoseconds;

  return parsed.add(roundedDuration);
}

/**
 * Subtract duration from a date
 */
export function subtractFromDate(date: DateInput, duration: DurationLike): Temporal.PlainDate | null {
  const parsed = parseDate(date);
  if (!parsed) {
    return null;
  }

  // Temporal doesn't support fractional values for days, months, years
  // Round fractional values to avoid "unsupported fractional value" error
  const roundedDuration: DurationLike = {};

  if (duration.years !== undefined) roundedDuration.years = Math.round(duration.years);
  if (duration.months !== undefined) roundedDuration.months = Math.round(duration.months);
  if (duration.weeks !== undefined) roundedDuration.weeks = Math.round(duration.weeks);
  if (duration.days !== undefined) roundedDuration.days = Math.round(duration.days);
  if (duration.hours !== undefined) roundedDuration.hours = duration.hours;
  if (duration.minutes !== undefined) roundedDuration.minutes = duration.minutes;
  if (duration.seconds !== undefined) roundedDuration.seconds = duration.seconds;
  if (duration.milliseconds !== undefined) roundedDuration.milliseconds = duration.milliseconds;
  if (duration.microseconds !== undefined) roundedDuration.microseconds = duration.microseconds;
  if (duration.nanoseconds !== undefined) roundedDuration.nanoseconds = duration.nanoseconds;

  return parsed.subtract(roundedDuration);
}

/**
 * Check if date1 is before date2
 */
export function isBefore(date1: DateInput, date2: DateInput): boolean {
  const parsed1 = parseDate(date1);
  const parsed2 = parseDate(date2);
  // Return false if either date is null to match moment.js behavior
  if (!parsed1 || !parsed2) {
    return false;
  }
  return Temporal.PlainDate.compare(parsed1, parsed2) < 0;
}

/**
 * Check if date1 is after date2
 */
export function isAfter(date1: DateInput, date2: DateInput): boolean {
  const parsed1 = parseDate(date1);
  const parsed2 = parseDate(date2);
  // Return false if either date is null to match moment.js behavior
  if (!parsed1 || !parsed2) {
    return false;
  }
  return Temporal.PlainDate.compare(parsed1, parsed2) > 0;
}

/**
 * Check if date1 is same as date2
 */
export function isSame(date1: DateInput, date2: DateInput): boolean {
  const parsed1 = parseDate(date1);
  const parsed2 = parseDate(date2);
  // Return false if either date is null to match moment.js behavior
  if (!parsed1 || !parsed2) {
    return false;
  }
  return Temporal.PlainDate.compare(parsed1, parsed2) === 0;
}

/**
 * Check if date1 is same or before date2
 */
export function isSameOrBefore(date1: DateInput, date2: DateInput): boolean {
  const parsed1 = parseDate(date1);
  const parsed2 = parseDate(date2);
  // Return false if either date is null to match moment.js behavior
  if (!parsed1 || !parsed2) {
    return false;
  }
  return Temporal.PlainDate.compare(parsed1, parsed2) <= 0;
}

/**
 * Check if date1 is same or after date2
 */
export function isSameOrAfter(date1: DateInput, date2: DateInput): boolean {
  const parsed1 = parseDate(date1);
  const parsed2 = parseDate(date2);
  // Return false if either date is null to match moment.js behavior
  if (!parsed1 || !parsed2) {
    return false;
  }
  return Temporal.PlainDate.compare(parsed1, parsed2) >= 0;
}

/**
 * Get difference between two dates
 */
export function dateDiff(startDate: DateInput, endDate: DateInput, unit: 'days' | 'months' | 'years' = 'days'): number {
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  // Return 0 if either date is null to match moment.js behavior
  if (!start || !end) {
    return 0;
  }

  const duration = start.until(end, { largestUnit: unit });

  switch (unit) {
    case 'days':
      return duration.days;
    case 'months':
      return duration.months;
    case 'years':
      return duration.years;
    default:
      return duration.days;
  }
}

/**
 * Get total difference (with decimals) between two dates
 */
export function dateDiffTotal(
  startDate: DateInput,
  endDate: DateInput,
  unit: 'days' | 'months' | 'years' = 'days',
): number {
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  // Return 0 if either date is null to match moment.js behavior
  if (!start || !end) {
    return 0;
  }

  const duration = start.until(end, { largestUnit: unit });

  // For months and years, we need to provide a relativeTo parameter
  // to handle the varying lengths of months/years
  if (unit === 'months' || unit === 'years') {
    return duration.total({ unit, relativeTo: start });
  }

  return duration.total(unit);
}

/**
 * Get start of day
 */
export function startOfDay(date: DateInput): Temporal.PlainDateTime | null {
  const parsed = parseDate(date);
  if (!parsed) {
    return null;
  }
  return parsed.toPlainDateTime({ hour: 0, minute: 0, second: 0 });
}

/**
 * Get end of day (23:59:59)
 */
export function endOfDay(date: DateInput): Temporal.PlainDateTime | null {
  const parsed = parseDate(date);
  if (!parsed) {
    return null;
  }
  return parsed.toPlainDateTime({ hour: 23, minute: 59, second: 59 });
}

/**
 * Get start of month
 */
export function startOfMonth(date: DateInput): Temporal.PlainDate | null {
  const plainDate = parseDate(date);
  if (!plainDate) {
    return null;
  }
  return plainDate.with({ day: 1 });
}

/**
 * Get end of month
 */
export function endOfMonth(date: DateInput): Temporal.PlainDate | null {
  const plainDate = parseDate(date);
  if (!plainDate) {
    return null;
  }
  const nextMonth = plainDate.add({ months: 1 }).with({ day: 1 });
  return nextMonth.subtract({ days: 1 });
}

/**
 * Get start of year
 */
export function startOfYear(date: DateInput): Temporal.PlainDate | null {
  const plainDate = parseDate(date);
  if (!plainDate) {
    return null;
  }
  return plainDate.with({ month: 1, day: 1 });
}

/**
 * Get unix timestamp in seconds
 */
export function toUnixSeconds(dateTime: DateTimeInput | number): number {
  const instant = parseInstant(dateTime);
  if (!instant) {
    return 0; // Return 0 for invalid dates to match moment.js behavior
  }
  return Math.floor(Number(instant.epochNanoseconds / BigInt(1000000000)));
}

/**
 * Get unix timestamp in milliseconds
 */
export function toUnixMilliseconds(dateTime: DateTimeInput | number): number {
  const instant = parseInstant(dateTime);
  if (!instant) {
    return 0; // Return 0 for invalid dates to match moment.js behavior
  }
  return instant.epochMilliseconds;
}

/**
 * Create date from unix timestamp
 */
export function fromUnixSeconds(seconds: number): Temporal.Instant {
  return Temporal.Instant.fromEpochNanoseconds(BigInt(seconds) * BigInt(1000000000));
}

/**
 * Create date from unix milliseconds
 */
export function fromUnixMilliseconds(milliseconds: number): Temporal.Instant {
  return Temporal.Instant.fromEpochMilliseconds(milliseconds);
}

/**
 * Check if a date string is valid
 */
export function isValidDate(dateString: string): boolean {
  try {
    Temporal.PlainDate.from(dateString);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if a datetime string is valid
 */
export function isValidDateTime(dateTimeString: string): boolean {
  try {
    Temporal.PlainDateTime.from(dateTimeString);
    return true;
  } catch {
    try {
      Temporal.Instant.from(dateTimeString);
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Convert to ISO string
 */
export function toISOString(dateTime: DateTimeInput): string {
  const instant = parseInstant(dateTime);
  if (!instant) {
    return ''; // Return empty string for invalid dates to match moment.js behavior
  }
  return instant.toString();
}

/**
 * Get timezone offset string (e.g., '+05:30')
 */
export function getTimezoneOffset(timezone = 'UTC'): string {
  const zdt = Temporal.Now.zonedDateTimeISO(timezone);
  const offsetNs = zdt.offsetNanoseconds;
  const offsetMinutes = offsetNs / 60000000000;
  const hours = Math.floor(Math.abs(offsetMinutes) / 60);
  const minutes = Math.abs(offsetMinutes) % 60;
  const sign = offsetMinutes >= 0 ? '+' : '-';
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Legacy support: Create a moment-like chain API for gradual migration
 */
export class TemporalChain {
  private value: Temporal.PlainDate | Temporal.PlainDateTime | Temporal.Instant;
  private explicitlySetTime: boolean = false;

  constructor(input?: DateTimeInput) {
    if (!input) {
      this.value = Temporal.Now.instant();
    } else if (typeof input === 'string' || input instanceof Date) {
      const parsedInstant = parseInstant(input);
      if (parsedInstant) {
        this.value = parsedInstant;
      } else {
        const parsedDateTime = parseDateTime(input);
        if (parsedDateTime) {
          this.value = parsedDateTime;
        } else {
          const parsed = parseDate(input);
          if (!parsed) {
            throw new Error('Invalid date input');
          }
          this.value = parsed;
        }
      }
    } else {
      this.value = input as Temporal.PlainDate | Temporal.PlainDateTime | Temporal.Instant;
    }
  }

  format(formatStr?: string): string {
    if (this.value instanceof Temporal.PlainDate) {
      return formatDate(this.value, formatStr);
    }
    if (this.value instanceof Temporal.PlainDateTime) {
      // When PlainDateTime was created from a date-only string, format as date
      // Check if time is midnight (00:00:00) to determine if it should be formatted as date only
      // But if time was explicitly set (via startOf), show the time
      if (
        this.value.hour === 0 &&
        this.value.minute === 0 &&
        this.value.second === 0 &&
        !formatStr &&
        !this.explicitlySetTime
      ) {
        return formatDate(this.value.toPlainDate());
      }
      return formatDateTime(this.value);
    }
    if (this.value instanceof Temporal.Instant) {
      return formatDateTime(this.value.toZonedDateTimeISO('UTC').toPlainDateTime());
    }
    // This should never be reached, but TypeScript needs exhaustiveness
    return '';
  }

  add(amount: number, unit: string): TemporalChain {
    const duration: Record<string, number> = {};
    duration[unit] = amount;

    if (this.value instanceof Temporal.PlainDate) {
      this.value = this.value.add(duration);
    } else if (this.value instanceof Temporal.PlainDateTime) {
      this.value = this.value.add(duration);
    } else if (this.value instanceof Temporal.Instant) {
      const zdt = this.value.toZonedDateTimeISO('UTC');
      this.value = zdt.add(duration).toInstant();
    }

    return this;
  }

  subtract(amount: number, unit: string): TemporalChain {
    const duration: Record<string, number> = {};
    duration[unit] = amount;

    if (this.value instanceof Temporal.PlainDate) {
      this.value = this.value.subtract(duration);
    } else if (this.value instanceof Temporal.PlainDateTime) {
      this.value = this.value.subtract(duration);
    } else if (this.value instanceof Temporal.Instant) {
      const zdt = this.value.toZonedDateTimeISO('UTC');
      this.value = zdt.subtract(duration).toInstant();
    }

    return this;
  }

  unix(): number {
    // Use toUnixSeconds to ensure consistent behavior with offset
    if (this.value instanceof Temporal.Instant) {
      return toUnixSeconds(this.value);
    }
    if (this.value instanceof Temporal.PlainDateTime) {
      return toUnixSeconds(this.value.toZonedDateTime('UTC').toInstant());
    }
    if (this.value instanceof Temporal.PlainDate) {
      return toUnixSeconds(this.value.toZonedDateTime('UTC').toInstant());
    }
    return 0;
  }

  valueOf(): number {
    // Use toUnixMilliseconds to ensure consistent behavior with offset
    if (this.value instanceof Temporal.Instant) {
      return toUnixMilliseconds(this.value);
    }
    if (this.value instanceof Temporal.PlainDateTime) {
      return toUnixMilliseconds(this.value.toZonedDateTime('UTC').toInstant());
    }
    if (this.value instanceof Temporal.PlainDate) {
      return toUnixMilliseconds(this.value.toZonedDateTime('UTC').toInstant());
    }
    return 0;
  }

  startOf(unit: string): TemporalChain {
    if (unit === 'day') {
      if (this.value instanceof Temporal.PlainDate) {
        this.value = this.value.toPlainDateTime({ hour: 0, minute: 0, second: 0 });
        this.explicitlySetTime = true;
      } else if (this.value instanceof Temporal.PlainDateTime) {
        this.value = this.value.with({ hour: 0, minute: 0, second: 0 });
        this.explicitlySetTime = true;
      }
    }
    return this;
  }

  isBefore(other: DateTimeInput): boolean {
    if (this.value instanceof Temporal.PlainDate) {
      return isBefore(this.value, other);
    }
    if (this.value instanceof Temporal.PlainDateTime) {
      return isBefore(this.value.toPlainDate(), other);
    }
    if (this.value instanceof Temporal.Instant) {
      return isBefore(this.value.toZonedDateTimeISO('UTC').toPlainDate(), other);
    }
    return false;
  }

  isAfter(other: DateTimeInput): boolean {
    if (this.value instanceof Temporal.PlainDate) {
      return isAfter(this.value, other);
    }
    if (this.value instanceof Temporal.PlainDateTime) {
      return isAfter(this.value.toPlainDate(), other);
    }
    if (this.value instanceof Temporal.Instant) {
      return isAfter(this.value.toZonedDateTimeISO('UTC').toPlainDate(), other);
    }
    return false;
  }

  isSameOrAfter(other: DateTimeInput): boolean {
    if (this.value instanceof Temporal.PlainDate) {
      return isSameOrAfter(this.value, other);
    }
    if (this.value instanceof Temporal.PlainDateTime) {
      return isSameOrAfter(this.value.toPlainDate(), other);
    }
    if (this.value instanceof Temporal.Instant) {
      return isSameOrAfter(this.value.toZonedDateTimeISO('UTC').toPlainDate(), other);
    }
    return false;
  }

  isSameOrBefore(other: DateTimeInput): boolean {
    if (this.value instanceof Temporal.PlainDate) {
      return isSameOrBefore(this.value, other);
    }
    if (this.value instanceof Temporal.PlainDateTime) {
      return isSameOrBefore(this.value.toPlainDate(), other);
    }
    if (this.value instanceof Temporal.Instant) {
      return isSameOrBefore(this.value.toZonedDateTimeISO('UTC').toPlainDate(), other);
    }
    return false;
  }

  diff(other: DateTimeInput, unit: string): number {
    if (this.value instanceof Temporal.PlainDate) {
      return dateDiff(other, this.value, unit as 'days' | 'months' | 'years');
    }
    if (this.value instanceof Temporal.PlainDateTime) {
      return dateDiff(other, this.value.toPlainDate(), unit as 'days' | 'months' | 'years');
    }
    if (this.value instanceof Temporal.Instant) {
      return dateDiff(other, this.value.toZonedDateTimeISO('UTC').toPlainDate(), unit as 'days' | 'months' | 'years');
    }
    return 0;
  }

  toDate(): Date {
    if (this.value instanceof Temporal.Instant) {
      return new Date(this.value.epochMilliseconds);
    }
    if (this.value instanceof Temporal.PlainDateTime) {
      return new Date(this.value.toZonedDateTime('UTC').epochMilliseconds);
    }
    if (this.value instanceof Temporal.PlainDate) {
      return new Date(this.value.toZonedDateTime('UTC').epochMilliseconds);
    }
    return new Date();
  }
}

/**
 * Helper function to create a TemporalChain (moment-like API)
 * This is for gradual migration - prefer using direct functions
 */
export function temporal(input?: DateTimeInput): TemporalChain {
  return new TemporalChain(input);
}

// Export Temporal types for convenience
export { Temporal };
